export type TtsWord = {
  startOffset: number;
  endOffset: number;
  textOffset: number;
  textLength: number;
  text?: string;
};

export type TtsSentence = {
  startOffset: number;
  endOffset: number;
  textOffset: number;
  textLength: number;
  text?: string;
  words: TtsWord[];
};

export type TtsSelectorElement = {
  selector: string;
  sentences: TtsSentence[];
};

export type TtsHighlightAtTime = {
  dataId: string;
  sentence: TtsSentence;
  word: TtsWord;
};

export function findTtsHighlightAtTime(
  elements: TtsSelectorElement[],
  currentTimeMs: number,
): TtsHighlightAtTime | null {
  for (const element of elements) {
    const dataId = dataIdFromTtsSelector(element.selector);
    if (!dataId || !element.sentences?.length) {
      continue;
    }
    for (const sentence of element.sentences) {
      if (!sentence.words?.length) {
        continue;
      }
      if (currentTimeMs < sentence.startOffset || currentTimeMs > sentence.endOffset) {
        continue;
      }
      for (const word of sentence.words) {
        if (word.startOffset <= currentTimeMs && currentTimeMs <= word.endOffset) {
          return { dataId, sentence, word };
        }
      }
    }
  }
  return null;
}

/**
 * Native apps push spotlight at each word's audio start time. Return every word
 * whose startOffset has been crossed since the last applied word, in order.
 */
export function findTtsWordsStartedSince(
  elements: TtsSelectorElement[],
  currentTimeMs: number,
  lastAppliedWordAudioStartMs: number,
): TtsHighlightAtTime[] {
  const matches: TtsHighlightAtTime[] = [];

  for (const element of elements) {
    const dataId = dataIdFromTtsSelector(element.selector);
    if (!dataId || !element.sentences?.length) {
      continue;
    }
    for (const sentence of element.sentences) {
      if (!sentence.words?.length) {
        continue;
      }
      for (const word of sentence.words) {
        if (word.startOffset > lastAppliedWordAudioStartMs && word.startOffset <= currentTimeMs) {
          matches.push({ dataId, sentence, word });
        }
      }
    }
  }

  return matches.sort((a, b) => a.word.startOffset - b.word.startOffset);
}

/** Largest word audio start at or before `currentTimeMs` (for seek/resume). */
export function findLatestTtsWordAudioStartAtOrBefore(
  elements: TtsSelectorElement[] | null | undefined,
  currentTimeMs: number,
): number {
  let latest = -1;

  if (!elements?.length) {
    return latest;
  }

  for (const element of elements) {
    if (!element.sentences?.length) {
      continue;
    }
    for (const sentence of element.sentences) {
      for (const word of sentence.words ?? []) {
        if (word.startOffset <= currentTimeMs && word.startOffset > latest) {
          latest = word.startOffset;
        }
      }
    }
  }

  return latest;
}

export function dataIdFromTtsSelector(selector: string | undefined): string | undefined {
  if (selector == null || selector === "") return undefined;
  const s = selector.trim();
  const m = s.match(/^\[id="([^"]*)"\]$/);
  return m ? m[1] : s;
}

// Truncated TTS marks leave word-less sentences pinned at audio end; seeking there auto-advances.
function isTimedTtsSentence(sentence: TtsSentence): boolean {
  return Boolean(sentence.words?.length) || sentence.endOffset > sentence.startOffset;
}

function findTtsElement(
  elements: TtsSelectorElement[],
  dataElementId: string,
): TtsSelectorElement | undefined {
  return elements.find((el) => dataIdFromTtsSelector(el.selector ?? "") === dataElementId);
}

function closestWordStartMs(element: TtsSelectorElement, textOffset: number): number | null {
  let best: { startMs: number; distance: number } | null = null;

  for (const sentence of element.sentences ?? []) {
    for (const word of sentence.words ?? []) {
      const distance = Math.abs(word.textOffset - textOffset);
      if (!best || distance < best.distance) {
        best = { startMs: word.startOffset, distance };
      }
    }
  }

  return best?.startMs ?? null;
}

export function findAudioStartMsForTextOffset(
  elements: TtsSelectorElement[],
  dataElementId: string,
  textOffset: number,
): number | null {
  const element = findTtsElement(elements, dataElementId);
  if (!element?.sentences?.length) {
    return null;
  }

  for (const sentence of element.sentences) {
    for (const word of sentence.words ?? []) {
      const wordEnd = word.textOffset + word.textLength;
      if (textOffset >= word.textOffset && textOffset < wordEnd) {
        return word.startOffset;
      }
    }
  }

  let next: { startMs: number; at: number } | null = null;
  for (const sentence of element.sentences) {
    for (const word of sentence.words ?? []) {
      if (word.textOffset >= textOffset) {
        if (!next || word.textOffset < next.at) {
          next = { startMs: word.startOffset, at: word.textOffset };
        }
      }
    }
  }
  if (next) {
    return next.startMs;
  }

  for (const sentence of element.sentences) {
    const sEnd = sentence.textOffset + sentence.textLength;
    if (
      isTimedTtsSentence(sentence) &&
      textOffset >= sentence.textOffset &&
      textOffset < sEnd
    ) {
      return sentence.startOffset;
    }
  }

  // Offset drift between DOM selection and TTS metadata — snap to nearest word.
  return closestWordStartMs(element, textOffset);
}
